import React, { useState } from "react";
import Header from "../components/Header";
import { requestLink, formatApiErrorDetail } from "../lib/auth";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Lock, Mail, Loader2, CheckCircle2 } from "lucide-react";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      await requestLink(email.trim());
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not send link.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header active="admin" />
      <main className="max-w-md mx-auto px-6 py-20">
        <Card data-testid="admin-login-card">
          <CardContent className="p-8">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <h1 className="font-display font-700 text-2xl text-foreground">Admin sign-in</h1>
            <p className="text-muted-foreground text-sm mt-1.5">
              Enter your approved email and we'll send you a secure sign-in link.
            </p>

            {sent ? (
              <div data-testid="login-sent-message" className="mt-6 rounded-xl bg-accent p-5 flex gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <p className="font-600 text-foreground">Check your inbox</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    If <span className="font-medium">{email}</span> is approved, a sign-in link is on its way.
                    The link expires in 15 minutes.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="admin-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="admin-email"
                      data-testid="admin-email-input"
                      type="email"
                      placeholder="you@organization.ca"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  {error && <p data-testid="login-error" className="text-xs text-destructive">{error}</p>}
                </div>
                <Button
                  type="submit"
                  data-testid="send-link-btn"
                  disabled={sending}
                  className="w-full gap-2"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  Send me a sign-in link
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
